/** Copy for campaign and contact state. The backend's enum names never reach the screen. */

export const CAMPAIGN_STATUS = {
  running: { label: "Running", className: "bg-green-100 text-green-800" },
  paused: { label: "Paused", className: "bg-amber-100 text-amber-800" },
  completed: { label: "Finished", className: "bg-blue-100 text-blue-800" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-500" },
};

export const CONTACT_STATUS = {
  queued: { label: "Waiting", className: "bg-gray-100 text-gray-700" },
  scheduled: { label: "Calling again", className: "bg-indigo-100 text-indigo-800" },
  calling: { label: "Calling now", className: "bg-green-100 text-green-800" },
  completed: { label: "Done", className: "bg-blue-100 text-blue-800" },
  failed: { label: "Could not dial", className: "bg-red-100 text-red-700" },
  skipped: { label: "Skipped", className: "bg-gray-100 text-gray-500" },
  // Only the queue view reports this: the lead is fine, its campaign is not moving.
  paused: { label: "Paused", className: "bg-amber-100 text-amber-800" },
};

export const OUTCOME_LABELS = {
  CONFIRMED: "Booked",
  TRANSFERRED: "Transferred to clinic",
  REJECTED_ALL_OPTIONS: "Declined",
  NEEDS_RESCHEDULE: "Wants a callback",
  NO_ANSWER: "No answer",
  BUSY: "Busy",
  VOICEMAIL: "Voicemail",
  CALLBACK_REQUESTED: "Asked us to call back",
  WRONG_NUMBER: "Wrong number",
  UNREACHED: "Never reached",
  INCOMPLETE: "Cut off",
  FAILED: "Could not dial",
};

export const SKIP_REASONS = {
  do_not_call: "Marked do not call",
  long_dormant: "Not seen in 18+ months",
  inactive_pms: "Inactive in the PMS",
  cooldown: "Contacted recently",
  in_campaign: "Already in a campaign",
  no_phone: "No phone number",
};

/**
 * Why the engine skipped a contact at dial time, keyed by the contact's
 * `error`. Only the reasons with their own copy are here; any other skip
 * shows the plain "Skipped" pill with its error under it.
 */
export const DIAL_SKIP_LABELS = {
  "already booked": "Skipped: already booked",
};

export const SMS_STATUS = {
  not_applicable: "",
  pending_confirmation: "Waiting on the booking",
  sent: "Text sent",
  delivered: "Text delivered",
  failed: "Text failed",
};

/** What to show in a contact's outcome column. */
export function outcomeLabel(contact) {
  if (contact.outcome) return OUTCOME_LABELS[contact.outcome] || contact.outcome;
  if (contact.status === "skipped" && DIAL_SKIP_LABELS[contact.error]) {
    return DIAL_SKIP_LABELS[contact.error];
  }
  return CONTACT_STATUS[contact.status]?.label || contact.status;
}

/**
 * Pill colours for a contact's outcome column. A booking reads green, whatever
 * the status; so does a transfer, which is a transfer campaign's booking.
 */
export function outcomeClassName(contact) {
  if (contact.outcome === "CONFIRMED" || contact.outcome === "TRANSFERRED") {
    return "bg-green-100 text-green-800";
  }
  return CONTACT_STATUS[contact.status]?.className || "bg-gray-100 text-gray-700";
}

/** Counters worth showing on a campaign card, in reading order. */
export const COUNTER_ORDER = [
  ["booked", "Booked"],
  ["transferred", "Transferred"],
  ["wrong_number", "Wrong number"],
  ["declined", "Declined"],
  ["wants_callback", "Wants a callback"],
  ["unreached", "Never reached"],
  ["no_answer", "No answer"],
  ["busy", "Busy"],
  ["voicemail", "Voicemail"],
  ["callbacks", "Callbacks scheduled"],
  ["cut_off", "Cut off"],
  ["failed", "Could not dial"],
  ["skipped", "Skipped"],
  ["scheduled", "Calling again"],
  ["queued", "Waiting"],
];

/**
 * How a campaign gets a patient in: a transfer hands the call to the clinic's
 * own line; everything else books through the PMS. Rows written before modes
 * existed carry none and were all booking campaigns.
 */
export function modeLabel(campaign) {
  return campaign?.mode === "transfer" ? "Transfer to clinic" : "Books via PMS";
}

/** What to show for a contact's calling slot. */
export const SLOT_LABELS = { morning: "Morning", afternoon: "Afternoon", evening: "Evening" };

/**
 * How far along a contact's attempts are. A contact with a scheduled next
 * attempt says when it is coming -- "next" for a plain retry, "Calling back"
 * when the patient asked to be called at that time.
 */
export function attemptLabel(contact) {
  const max = contact.max_attempts || 1;
  if (!contact.attempt_no) return "Not called yet";
  if (contact.status === "scheduled" && contact.next_attempt_at) {
    const verb = contact.next_attempt_kind === "callback" ? "Calling back" : "next";
    return `Attempt ${contact.attempt_no} of ${max} · ${verb} ${formatWireTime(contact.next_attempt_at)}`;
  }
  return `${contact.attempt_no} of ${max} attempts`;
}

/** `UTC`, `UTC+03:00`, `UTC-05:30`. */
function offsetLabel(minutes) {
  if (minutes === 0) return "UTC";
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  const pad = (n) => String(n).padStart(2, "0");
  return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * Render a backend timestamp on the clock it was written on.
 *
 * `next_dial_at` is the clinic's next calling slot and arrives with the
 * clinic's own UTC offset. `new Date(x).toLocaleString()` silently relabels
 * it in the viewer's zone, so an admin abroad reads "07:00" for a clinic
 * whose calling window opens at 10:00 and concludes the scheduler is broken.
 * The response carries no IANA zone name, so the offset is all we honestly
 * have: keep the clinic's wall clock and print the offset beside it.
 *
 * A value with no offset at all has no clinic clock to preserve, so it falls
 * back to the viewer's locale; an unparseable one is passed through.
 */
export function formatWireTime(value) {
  if (!value) return "";
  const raw = String(value).trim();
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  const match = /([+-])(\d{2}):?(\d{2})$|(Z)$/.exec(raw);
  if (!match) return date.toLocaleString();

  const offsetMinutes = match[4]
    ? 0
    : (match[1] === "-" ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
  const onClinicClock = new Date(date.getTime() + offsetMinutes * 60000);
  const clock = onClinicClock.toLocaleString("en-US", {
    timeZone: "UTC", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
  return `${clock} ${offsetLabel(offsetMinutes)}`;
}
